import React, { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChatBubble } from '@/components/ChatBubble';
import { Sidebar } from '@/components/Sidebar';
import { PhaseIndicator } from '@/components/PhaseIndicator';
import PageTransition from '@/components/PageTransition';
import { useInterview } from '@/hooks/useInterview';
import { InterviewWelcomeBanner } from '@/components/InterviewWelcomeBanner';
import { InterviewFormPanel } from '@/components/InterviewFormPanel';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const Chat = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Form panel state
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [phaseCompleted, setPhaseCompleted] = useState(0);

  const {
    messages,
    sendMessage,
    isLoading,
    isInitializing,
    isComplete,
    interviewId,
    error,
    completionPercentage,
    restartInterview,
    reinitialize,
  } = useInterview();

  // Map messages to the format expected by ChatBubble
  const formattedMessages = messages.map(msg => ({
    id: msg.id,
    content: msg.content,
    isUser: msg.isUser,
    timestamp: 'Just now',
  }));

  // Load phase status and control form collapse
  useEffect(() => {
    const checkPhaseStatus = async () => {
      if (!interviewId) return;

      const { data, error } = await supabase
        .from('interviews')
        .select('phase_completed')
        .eq('id', interviewId)
        .single();

      if (error) {
        console.error('Error checking phase:', error);
        return;
      }

      if (data) {
        const phase = data.phase_completed ?? 0;
        setPhaseCompleted(phase);
        // Auto-collapse form if Phase 1 is complete
        if (phase >= 1) {
          setIsFormCollapsed(true);
        }
      }
    };

    checkPhaseStatus();
  }, [interviewId]);

  // Auto-navigate to research phase when interview completes
  useEffect(() => {
    if (isComplete && interviewId) {
      console.log('✅ Interview complete! Navigating to plan generation:', interviewId);
      setTimeout(() => {
        navigate(`/plan-generation/${interviewId}`);
      }, 2000);
    }
  }, [isComplete, interviewId, navigate]);

  // Auto-scroll to bottom when new messages arrive
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    const messageContainer = messagesEndRef.current?.parentElement;
    if (!messageContainer) return;

    const isNearBottom = 
      messageContainer.scrollHeight - messageContainer.scrollTop - messageContainer.clientHeight < 100;

    if (formattedMessages.length > prevMessageCountRef.current && isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    prevMessageCountRef.current = formattedMessages.length;
  }, [formattedMessages.length]);

  // Focus input after initialization
  useEffect(() => {
    if (!isInitializing && inputRef.current && phaseCompleted >= 1) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isInitializing, phaseCompleted]);

  const handleSendMessage = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!input.trim() || isLoading || isComplete) return;

    const currentInput = input;
    setInput('');
    sendMessage(currentInput);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFormComplete = async () => {
    setPhaseCompleted(1);
    setIsFormCollapsed(true);
    toast.success('Information saved! Starting your personalized interview...');
    
    // Reinitialize the hook to trigger AI greeting
    await reinitialize();
  };

  return (
    <PageTransition>
      <div className="flex h-screen overflow-hidden bg-background">
        <Sidebar />

        <InterviewFormPanel
          isCollapsed={isFormCollapsed}
          onToggle={() => setIsFormCollapsed(!isFormCollapsed)}
          onFormComplete={handleFormComplete}
          interviewId={interviewId}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="h-16 border-b border-border/40 flex items-center justify-between px-6 bg-background/95 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="hover:bg-muted/50"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Emergency Planning Interview</h1>
                <p className="text-sm text-muted-foreground">
                  {phaseCompleted >= 1 
                    ? `${completionPercentage}% complete — gathering personalized details` 
                    : "Fill out the form to begin the interview"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={restartInterview}
                disabled={isInitializing || isLoading}
              >
                Restart Interview
              </Button>
              <PhaseIndicator currentPhase={phaseCompleted >= 1 ? 2 : 1} />
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto px-6 py-4 space-y-4">
              {phaseCompleted < 1 && (
                <div className="max-w-3xl mx-auto my-8 p-6 bg-muted/30 rounded-lg border border-border">
                  <h2 className="text-lg font-semibold mb-2">👈 Start Here</h2>
                  <p className="text-muted-foreground">
                    Please fill out the basic information form on the left to begin your personalized emergency planning interview.
                  </p>
                </div>
              )}

              {phaseCompleted >= 1 && formattedMessages.length === 1 && !isLoading && (
                <InterviewWelcomeBanner />
              )}

              {phaseCompleted >= 1 && formattedMessages.map((message) => (
                <ChatBubble
                  key={message.id}
                  content={message.content}
                  isUser={message.isUser}
                  timestamp={message.timestamp}
                />
              ))}

              {phaseCompleted >= 1 && isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted/50 rounded-lg px-4 py-3 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive">
                  <p className="font-medium">Error</p>
                  <p className="text-sm mt-1">{error}</p>
                  {error.includes('sign in') && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => navigate('/login')}
                    >
                      Go to Login
                    </Button>
                  )}
                </div>
              )}

              {isComplete && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-primary">
                  <p className="font-medium">Interview Complete! 🎉</p>
                  <p className="text-sm mt-1">Redirecting to plan generation...</p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {phaseCompleted >= 1 && (
            <div className="border-t border-border/40 bg-muted/30 backdrop-blur-sm">
              <div className="px-6 py-4">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="flex-1"
                    disabled={isLoading || isComplete}
                  />
                  <Button
                    type="submit"
                    disabled={!input.trim() || isLoading || isComplete}
                    size="icon"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

export default Chat;
